import HPOCategorization from '@/features/terminology/hpo/HPOCategorization'
import HPOSearch from '@/features/terminology/hpo/HPOSearchClient'
import { CategorizedHPOOption, HPOOption, RequisitionFormData } from '@/types/RequisitionFormTypes'
import { Button } from '@helix-ds/react'
import { ExclamationCircle } from 'flowbite-react-icons/outline'
import React, { useEffect, useState } from 'react'

interface HPOTermsProps {
  formData?: RequisitionFormData | null
  onUpdate: (hpoTerms: CategorizedHPOOption[]) => void | Promise<void>
  onCancel: () => void
  onBack: () => void
  onUnsavedChangesCheck: (hasChanges: boolean) => void
  isUpdating?: boolean
  readOnly?: boolean
}

const HPOTerms: React.FC<HPOTermsProps> = ({
  formData,
  onUpdate,
  onCancel,
  onBack,
  onUnsavedChangesCheck,
  isUpdating = false,
  readOnly = false,
}) => {
  const [hpoTerms, setHpoTerms] = useState<CategorizedHPOOption[]>([])
  const [originalHpoTerms, setOriginalHpoTerms] = useState<CategorizedHPOOption[]>([])

  // Initialize HPO terms from formData when component mounts or formData changes
  useEffect(() => {
    console.log('HPOTerms - formData:', formData)
    console.log('HPOTerms - formData.reason:', formData?.reason)
    console.log('HPOTerms - formData.reason.clinicalDiagnosis:', formData?.reason?.clinicalDiagnosis)
    console.log('HPOTerms - Array check:', Array.isArray(formData?.reason?.clinicalDiagnosis))
    console.log('HPOTerms - Length:', formData?.reason?.clinicalDiagnosis?.length)

    if (formData?.reason?.clinicalDiagnosis && Array.isArray(formData.reason.clinicalDiagnosis)) {
      const clinicalDiagnosis = formData.reason.clinicalDiagnosis
      console.log('HPOTerms - Setting HPO terms:', clinicalDiagnosis)
      console.log('HPOTerms - HPO terms count:', clinicalDiagnosis.length)

      // Create a deep copy to ensure state updates
      const hpoTermsCopy = clinicalDiagnosis.map((term) => ({ ...term }))
      setHpoTerms(hpoTermsCopy)
      setOriginalHpoTerms(hpoTermsCopy)
    } else {
      // Reset to empty if no data available
      console.log('HPOTerms - No HPO terms found, resetting to empty')
      setHpoTerms([])
      setOriginalHpoTerms([])
    }
  }, [formData])

  // Check if there are unsaved changes and notify parent
  useEffect(() => {
    const hasChanges = hasUnsavedChanges()
    onUnsavedChangesCheck(hasChanges)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hpoTerms, originalHpoTerms])

  // Check if there are unsaved changes
  const hasUnsavedChanges = (): boolean => {
    if (hpoTerms.length !== originalHpoTerms.length) return true

    // Check if terms or categories have changed
    const currentTermsMap = new Map(hpoTerms.map((term) => [term.id, term]))
    const originalTermsMap = new Map(originalHpoTerms.map((term) => [term.id, term]))

    // Check if any term IDs are different
    if (hpoTerms.some((term) => !originalTermsMap.has(term.id))) return true
    if (originalHpoTerms.some((term) => !currentTermsMap.has(term.id))) return true

    // Check if any categories have changed
    for (const term of hpoTerms) {
      const originalTerm = originalTermsMap.get(term.id)
      if (originalTerm && originalTerm.category !== term.category) return true
    }

    return false
  }

  // Helper function to convert CategorizedHPOOption[] to HPOOption[] for HPOSearch
  const getHPOOptions = (): HPOOption[] => {
    console.log('HPOTerms - getHPOOptions called, hpoTerms:', hpoTerms)
    console.log('HPOTerms - hpoTerms length:', hpoTerms.length)
    return hpoTerms.map((term) => ({
      id: term.id,
      label: term.label,
      node: term.node,
    }))
  }

  // HPO Terms handlers
  const handleHPOTermsChange = (selectedOptions: HPOOption[]) => {
    // Update categorized terms - preserve existing categories or default to 'Key'
    const existingTermIds = new Set(hpoTerms.map((term) => term.id))
    const newTerms = selectedOptions
      .filter((option) => !existingTermIds.has(option.id))
      .map((option) => ({
        ...option,
        category: 'Key' as const,
      }))

    // Keep existing categorized terms and add new ones
    const updatedCategorizedTerms: CategorizedHPOOption[] = [
      ...hpoTerms.filter((term) => selectedOptions.some((option) => option.id === term.id)),
      ...newTerms,
    ]

    setHpoTerms(updatedCategorizedTerms)
  }

  const handleHPOCategoryChange = (termId: string, newCategory: 'Key' | 'Additional' | 'Excluded') => {
    setHpoTerms((prevTerms) =>
      prevTerms.map((term) => (term.id === termId ? { ...term, category: newCategory } : term))
    )
  }

  const handleUpdate = async () => {
    try {
      // Wait for the async update operation to complete
      await onUpdate(hpoTerms)
      // Only update originalHpoTerms after successful update
      setOriginalHpoTerms(hpoTerms)
    } catch (error) {
      // If update fails, don't update originalHpoTerms
      // This ensures hasUnsavedChanges() still returns true, allowing user to retry
      console.error('Failed to update HPO terms:', error)
      // Error handling is done in the parent component, so we just need to prevent
      // updating originalHpoTerms here
    }
  }

  const handleCancelClick = () => {
    // Don't reset HPO terms here - let the parent handle the unsaved changes check
    // The parent will show a modal, and only reset if user confirms "Discard Changes"
    onCancel()
  }

  const handleBackClick = () => {
    onBack()
  }

  // Check if formData is available
  if (!formData || !formData.reason) {
    return (
      <div className="rounded-base border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 p-6 shadow-sm">
        <div className="text-center">
          <p className="text-sm leading-5 font-medium text-text-heading dark:text-white">
            Missing form data. Please ensure you navigated from a valid order flow.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Information Banner */}
      <div
        className={`rounded-base border p-4 ${readOnly ? 'border-border-base dark:border-gray-700 bg-background-secondary-medium dark:bg-gray-800' : 'border-info-100 dark:border-info-600 bg-info-50 dark:bg-gray-900'}`}
      >
        <p className="text-sm leading-5 font-medium text-text-heading dark:text-white">
          {readOnly
            ? 'Phenotype Information is locked. HPO terms cannot be modified once the sample begins processing.'
            : "Phenotype Information is Editable. You may update notes on the patient's phenotype via HPO terms at any time before sample is accessioned by the lab."}
        </p>
      </div>

      {/* Unsaved Changes Warning Banner - Only show when not read-only */}
      {!readOnly && hasUnsavedChanges() && (
        <div className="rounded-base border border-warning-300 dark:border-warning-600 bg-warning-50 dark:bg-gray-900 p-4">
          <div className="flex items-center gap-2">
            <ExclamationCircle className="h-5 w-5 text-text-fg-warning dark:text-warning-100" />
            <div>
              <p className="text-sm leading-5 font-medium text-text-heading dark:text-white">Phenotype updates are ready to send</p>
              <p className="text-xs leading-4 text-text-subtle dark:text-gray-500">You have unsaved changes to HPO terms.</p>
            </div>
          </div>
        </div>
      )}

      {/* HPO Terms Input Section - Only show search when not read-only */}
      {!readOnly && (
        <div className="rounded-base border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 p-6 shadow-sm">
          <label htmlFor="hpoTerms" className="block text-sm leading-5 font-medium text-text-body dark:text-gray-400">
            HPO Terms
          </label>
          <HPOSearch
            id="hpoTerms"
            value={getHPOOptions()}
            onChange={handleHPOTermsChange}
            placeholder="Add more HPO terms..."
            maxSelections={10}
          />
        </div>
      )}

      {/* Categorize HPO Terms Section */}
      {hpoTerms.length > 0 && (
        <div className="rounded-base border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 p-6 shadow-sm">
          <HPOCategorization
            categorizedTerms={hpoTerms}
            onCategoryChange={readOnly ? () => {} : handleHPOCategoryChange}
            title={readOnly ? 'HPO Terms' : 'Categorize HPO Terms'}
            readOnly={readOnly}
          />
        </div>
      )}

      {/* Footer Buttons - Only show when not read-only */}
      {!readOnly && (
        <div className="flex items-center justify-between border-t border-border-base dark:border-gray-700 pt-4">
          <button
            onClick={handleCancelClick}
            className="rounded-full border border-border-base-medium dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-2 text-sm font-medium text-text-body dark:text-gray-400 hover:bg-background-secondary-medium dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2"
          >
            Cancel
          </button>
          <div className="flex gap-3">
            <button
              onClick={handleBackClick}
              className="rounded-full border border-border-base-medium dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-2 text-sm font-medium text-text-body dark:text-gray-400 hover:bg-background-secondary-medium dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2"
            >
              Back
            </button>
            <Button
              variant="primary"
              size="md"
              onClick={handleUpdate}
              disabled={isUpdating}
              className="focus-visible:ring-brand-600"
            >
              {isUpdating ? (
                <span className="flex items-center gap-2">
                  {/* helix-exception: spinner kept; Flowbite Spinner adds role="status" and a label, an a11y change (F-13) */}
                  <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Updating...
                </span>
              ) : (
                'Update HPO'
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

export default HPOTerms
